import React from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * The file picker on a reported issue (ST-20260921-0002).
 *
 * Shared rather than copied, because these two values have to agree with
 * `server/services/supportAttachmentService.js` — a limit that drifts out of
 * step with the server rejects a file the server would have taken, or accepts
 * one it will refuse after the reporter has waited for the upload. There are
 * two report-issue entry points today and the training one was added later, so
 * a per-dialog copy was already on its way to being wrong somewhere.
 */
export const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

export const ACCEPTED_TYPES = [
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/gif',
  'application/pdf', 'text/plain', 'text/csv', 'application/json'
];

/**
 * @param {object|null} attachment  {filename, contentType, bytes} or null
 * @param {function} onChange       receives the attachment, or null when cleared
 * @param {function} onError        receives a message the dialog shows in its own Alert
 * @param {boolean} disabled        while the report is being submitted
 * @param {string} label            wording for the picker before a file is chosen
 */
const TicketAttachmentField = ({
  attachment,
  onChange,
  onError,
  disabled = false,
  label = 'Attach a screenshot (optional)'
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    // Clearing the picker clears the attachment; it must not leave the previous
    // file silently attached.
    if (!file) { onChange(null); return; }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      onError('Attachments can be an image, a PDF or a text file.');
      onChange(null);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      onError(`That file is ${Math.round(file.size / 1048576)} MB. The limit is ${MAX_ATTACHMENT_BYTES / 1048576} MB.`);
      onChange(null);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL gives "data:<type>;base64,<payload>"; the server stores
      // the payload alone.
      const payload = String(reader.result || '').split(',')[1] || '';
      onChange({ filename: file.name, contentType: file.type, bytes: payload });
      onError('');
    };
    reader.onerror = () => {
      onError('That file could not be read. You can submit without it.');
      onChange(null);
    };
    reader.readAsDataURL(file);
  };

  return (
    /* Optional, and labelled as such: the report is the thing that matters and
       a file helps, so nothing here should imply one is required. */
    <Box sx={{ mt: 2 }}>
      <Button
        component="label"
        variant="outlined"
        size="small"
        disabled={disabled}
        data-testid="support-ticket-attach"
      >
        {attachment ? 'Choose a different file' : label}
        <input
          type="file"
          hidden
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleFileChange}
        />
      </Button>
      {attachment && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
          data-testid="support-ticket-attachment-name"
        >
          {attachment.filename}
          {' · '}
          <Button
            size="small"
            disabled={disabled}
            onClick={() => onChange(null)}
            sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
          >
            remove
          </Button>
        </Typography>
      )}
    </Box>
  );
};

export default TicketAttachmentField;
